'use client';

import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLoaderData, useParams, useSearchParams, type LoaderFunctionArgs } from 'react-router-dom';
import GenericCatalogDetailClient from '../../components/generic-catalog-detail-client';
import { independentCatalogPage } from '../../components/catalog-independent/pages/registry';
import {
  fetchGames,
  normalizeGameConfig,
  type GameConfig,
} from '../../lib/game-config';
import {
  parseCatalogNavigation,
  type CatalogNavigationItem,
} from '../../lib/catalog-navigation';
import SpaHeroPage from './SpaHeroPage';
import { useRegisteredGame } from '../use-registered-game';

type CatalogsState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; catalogs: CatalogNavigationItem[] };

type BangdreamDetailPreload =
  | { status: 'ready'; game: GameConfig; catalog: CatalogNavigationItem; summary: Record<string, unknown>; detail: Record<string, unknown> }
  | { status: 'error'; message: string }
  | null;

export async function loadBangdreamDetail({ params }: LoaderFunctionArgs): Promise<BangdreamDetailPreload> {
  const route = String(params.route || '');
  if (params.game !== 'bangdream' || !/^b[1-9]\d*$/.test(route)) return null;
  try {
    const game = (await fetchGames(true)).find(item => item.key === 'bangdream');
    if (!game) throw new Error('游戏资料不存在');
    const catalogsResponse = await fetch('/api/games/bangdream/catalogs', { cache: 'no-store' });
    if (!catalogsResponse.ok) throw new Error('图鉴目录加载失败');
    const catalogsPayload = await catalogsResponse.json() as { catalogs?: Parameters<typeof parseCatalogNavigation>[1] };
    const catalog = parseCatalogNavigation(game.key, catalogsPayload.catalogs || []).find(item =>
      item.adapterKey === 'generic-json-v1' && item.displayMode === 'standalone' && item.detailPrefix === 'b');
    if (!catalog) throw new Error('角色图鉴不存在');
    const base = `/api/games/${encodeURIComponent(game.key)}/catalogs/${encodeURIComponent(catalog.catalogKey)}/generic/items`;
    const [listResponse, detailResponse] = await Promise.all([
      fetch(base, { cache: 'no-store' }),
      fetch(`${base}?id=${encodeURIComponent(route.slice(1))}`, { cache: 'no-store' }),
    ]);
    if (!listResponse.ok || !detailResponse.ok) throw new Error('角色资料加载失败');
    const list = await listResponse.json() as { items?: Record<string, unknown>[] };
    const detail = await detailResponse.json() as Record<string, unknown>;
    const summary = (list.items || []).find(item => String(item.id) === route.slice(1));
    if (!summary) throw new Error('角色不存在');
    return { status: 'ready', game, catalog, summary, detail };
  } catch (error) {
    return { status: 'error', message: error instanceof Error ? error.message : '角色资料加载失败' };
  }
}

/**
 * SPA 深链闸门：
 * - `t*` → 角色详情
 * - `[a-z][1-9]\\d*` → 通用/独立图鉴详情（按 catalogs.detailPrefix 匹配）
 * - 其它 → 回游戏首页
 */
export default function SpaGameDeepLinkPage() {
  const preloaded = useLoaderData() as BangdreamDetailPreload;
  const params = useParams();
  const [searchParams] = useSearchParams();
  const gameParam = String(params.game || '');
  const route = String(params.route || '');

  const registered = useRegisteredGame(gameParam);
  const game = useMemo((): GameConfig => {
    if (preloaded?.status === 'ready') return preloaded.game;
    if (registered.status === 'ready') {
      return normalizeGameConfig(registered.game);
    }
    return normalizeGameConfig({
      key: gameParam,
      domain: gameParam,
      name: gameParam,
    });
  }, [gameParam, registered, preloaded]);

  const flags = useMemo(() => {
    const offset = Number(searchParams.get('offset'));
    return {
      startAtContent: searchParams.get('view') === 'content',
      initialContentSection:
        searchParams.get('section') === 'archive'
          ? ('archive' as const)
          : ('skills' as const),
      initialSectionOffset: Number.isFinite(offset)
        ? Math.max(-128, Math.min(1000000, offset))
        : undefined,
    };
  }, [searchParams]);

  const genericMatch = /^([a-z])([1-9]\d*)$/.exec(route);
  const isHeroRoute = /^t/i.test(route);
  const [catalogsState, setCatalogsState] = useState<CatalogsState>({
    status: 'loading',
  });

  useEffect(() => {
    if (preloaded?.status !== 'ready') return;
    const name = String(preloaded.detail.name || preloaded.detail.title || preloaded.summary.name || '图鉴条目');
    document.title = `${name}｜${preloaded.catalog.name}｜${preloaded.game.name} Wiki`;
  }, [preloaded]);

  useEffect(() => {
    if (preloaded) return;
    // t* 走角色详情，不必等 catalogs；其它字母前缀才拉目录匹配 detailPrefix
    if (!genericMatch || !gameParam || isHeroRoute) {
      setCatalogsState({ status: 'ready', catalogs: [] });
      return;
    }
    if (registered.status !== 'ready') {
      setCatalogsState({ status: 'loading' });
      return;
    }
    const gameKey = registered.game.key || gameParam;
    let cancelled = false;
    setCatalogsState({ status: 'loading' });
    const load = async () => {
      try {
        const res = await fetch(
          `/api/games/${encodeURIComponent(gameKey)}/catalogs`,
          { cache: 'no-store', credentials: 'same-origin' },
        );
        if (!res.ok) throw new Error('图鉴目录加载失败');
        const payload = (await res.json()) as {
          catalogs?: Parameters<typeof parseCatalogNavigation>[1];
        };
        const catalogs = parseCatalogNavigation(
          gameKey,
          payload.catalogs || [],
        );
        if (!cancelled) setCatalogsState({ status: 'ready', catalogs });
      } catch (cause) {
        if (!cancelled) {
          setCatalogsState({
            status: 'error',
            message: cause instanceof Error ? cause.message : '加载失败',
          });
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameParam, route, isHeroRoute, registered.status, preloaded]);

  if (!gameParam || !route) {
    return <Navigate to="/" replace />;
  }

  if (preloaded?.status === 'error') {
    return <main className="spa-boot" role="alert"><h1>加载失败</h1><p>{preloaded.message}</p></main>;
  }
  if (preloaded?.status === 'ready') {
    const IndependentPage = independentCatalogPage(preloaded.game.key, preloaded.catalog.catalogKey);
    if (IndependentPage) return <IndependentPage game={preloaded.game.domain} catalogName={preloaded.catalog.name} summary={preloaded.summary} detail={preloaded.detail} />;
  }

  if (registered.status === 'error') {
    return (
      <main className="spa-boot" role="alert">
        <h1>加载失败</h1>
        <p>{registered.message}</p>
        <p>
          <a href="/">返回首页</a>
        </p>
      </main>
    );
  }

  // 与历史 SPA 一致：t* 优先角色详情（通用图鉴勿占用 t 前缀）
  if (isHeroRoute) {
    return <SpaHeroPage />;
  }

  if (genericMatch) {
    if (registered.status === 'loading' || catalogsState.status === 'loading') {
      return (
        <main className="spa-boot" aria-busy="true">
          <p className="spa-boot-status" />
        </main>
      );
    }
    if (catalogsState.status === 'error') {
      return (
        <main className="spa-boot" role="alert">
          <h1>加载失败</h1>
          <p>{catalogsState.message}</p>
          <p>
            <a href={`/${encodeURIComponent(gameParam)}`}>回首页</a>
          </p>
        </main>
      );
    }
    const catalog = catalogsState.catalogs.find(
      (item) =>
        item.adapterKey === 'generic-json-v1' &&
        item.displayMode === 'standalone' &&
        item.detailPrefix === genericMatch[1],
    );
    if (catalog) {
      return (
        <GenericCatalogDetailClient
          key={`${game.key}:${route}`}
          game={game}
          catalog={catalog}
          itemId={genericMatch[2]}
          route={route}
          {...flags}
        />
      );
    }
    return <Navigate to={`/${encodeURIComponent(gameParam)}`} replace />;
  }

  return <Navigate to={`/${encodeURIComponent(gameParam)}`} replace />;
}
